import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AddressService } from '@shared/service/address.service';
import { LogicService } from '../../../service/logic/logic.service';
import { StringVariableService } from '../../../service/string-variable/string-variable.service';
import { BuildingBlockElementsBaseComponent } from './building-block-elements-base.component';
import { FeatureFlagService } from '@cudirect/shared-ui-library';

const formatterCollection: { [id: string]: string } = {
  flexbox: 'flexbox-format',
  group: 'bl-margin-left--24',
  groupradio: 'bl-margin-left--24 bl-margin-top--16',
  inline: 'inline',
  'no-padding': 'bl-padding-right--0',
  'spacer-bottom': 'bl-margin-bottom--32',
  'spacer-top': 'bl-margin-top--32',
  'declaration-spacer-top': 'bl-margin-top--24',
  subgroup: 'bl-margin-left--48',
  bold: 'bl-font-size--22 bl-font-weight--800',
  spacertopradio: 'bl-margin-bottom--0',
  fieldWrap: 'bl-form-field-wrap',
  flexboxfullwidth: 'flexbox-full-width'
};

/**
 * Base class for all controls. Instantiated by building-block.component directly.
 * Responsible for initiating load on fields
 */
@Component({ template: '', standalone: false })
export class CoreBuildingBlockControlBaseComponent extends BuildingBlockElementsBaseComponent implements OnInit, OnDestroy {
  /**
   * Focused state of control
   */
  private _focused = false;

  constructor(
    protected readonly logicService: LogicService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly stringVariableService: StringVariableService,
    public readonly addressService: AddressService,
    public readonly featureFlagService: FeatureFlagService
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this.field) {
      /* This dirty check will avoid error message to show / hide card fields */
      this.field.dirty = false;

      if (this.field.data) {
        this.field.data.onChange(this.onDataChange);
        this.field.data.load((): void => {
          if (this.field.data.value == null) {
            this.field.data.value = this.field.config.defaultValue;
          }
          this.onLoad();
        });
      }
    }
  }

  public ngOnDestroy(): void {
    if (this.field?.data) {
      this.field.data.unsubscribe(this.onDataChange);
    }
  }

  /**
   * Builds the aria-labelledby string depending on if card is present.
   */
  public get fieldLabelledBy(): string {
    const cardPrefix = 'title-';
    // We should always have a field. If Field is null, we have problems.
    const fieldLabelId = this.field.id + '-label';
    let labelledBy = fieldLabelId;

    if (this.card?.id) {
      labelledBy += ' ' + cardPrefix + this.card.id;
    }

    return labelledBy;
  }

  /**
   * Focused state of control
   */
  public get focused(): boolean {
    // eslint-disable-next-line no-underscore-dangle
    return this._focused;
  }

  public get postfix(): string {
    return this.field.config?.postfix ? this.parse(this.field.config.postfix) : '';
  }

  /**
   * Focused state of control
   */
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set focused(value: boolean) {
    // eslint-disable-next-line no-underscore-dangle
    this._focused = value;
    if (value) {
      this.field.data.saveLock();
    } else {
      this.field.data.saveUnlock();
    }
  }

  /**
   * Returns a list of field's css classes set in config.
   */
  public get formatter(): string {
    const format = this.field.config.format;
    return Array.isArray(format) ? format.map((item) => formatterCollection[item]).join(' ') : formatterCollection[format];
  }

  /**
   * Set screen reader only class
   * @param field id
   */
  public screenReaderOnly(field): boolean {
    return field.config.labelVisible === false;
  }

  /**
   * Set nestedFields for radio/checkbox types to display after the selected option
   * @param field id
   */
  public isInnerLayout(field): boolean {
    return field.config.innerNestedLayout === true;
  }

  /**
   * Force detection check. Apparently data.value changes are not always picked up, this is a workaround
   */
  protected readonly onDataChange = (): void => {
    setTimeout(() => {
      this.changeDetectorRef.markForCheck();
    }, 1);
  };

  /**
   * onload callback
   */
  protected readonly onLoad = (): void => {
    if (this.field.config.onLoad) {
      this.logicService.evaluate(this.field.config.onLoad, {
        field: this.field,
        card: this.card,
        page: this.page
      });
    }
  };

  public onClick(clickEvent: Event): void {
    clickEvent.stopPropagation();
    this.logicService.evaluate(this.field.config.onClick, {
      field: this.field,
      card: this.card,
      page: this.page,
      checked: clickEvent?.target ? (clickEvent.target as HTMLInputElement).checked : false // for checkbox adding checked value to context
    });
  }

  /** parsed tooltip */
  public get tooltip(): string {
    return this.field.tooltip;
  }

  /**
   * read autocomplete property from field config. Default to true if not set.
   * this flag decides if the browser autocomplete suggestions will be displayed.
   */
  public get autoCompleteEnabled(): boolean {
    return this.field.config.autoComplete !== false;
  }

  /** parse string variables */
  public parse(text: string, token?: string, value?: string): string {
    return this.stringVariableService
      .parse(text, this.field.iterationId, this.field.pageIterationId)
      .toString()
      .replace(`{{${token}}}`, value);
  }
}
